import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Finalização manual de admissão/rescisão que não vai fechar pelo fluxo normal
// (desistência, colaborador não quis assinar...). A operadora SOLICITA ao gestor
// por e-mail — sempre com prévia antes de enviar — e só o adm FINALIZA (ou
// reabre). Finalizado, o processo conta como concluído em todo o Saber e para
// de apitar no Painel, na janela de atrasos e no boletim do gestor.
// Em vez de finalizar, o adm pode DEVOLVER à operadora com uma orientação de
// como resolver; ela vê um pop-up (ModalFinalizacaoDevolvida) com a orientação.
// Backend: core/views_finalizacao_rh.py (/api/bporh/finalizacao/).

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const ROTULO = { admissao: 'admissão', rescisao: 'rescisão' };

export default function FinalizacaoManual({ tipo, clienteId, conveniaId, pendentes = [], onMudou, abrirSolicitar = false }) {
  const [info, setInfo] = useState(null);
  const [modo, setModo] = useState(null);        // null | 'solicitar' | 'finalizar' | 'reabrir' | 'devolver'
  const [motivo, setMotivo] = useState('');
  const [operadorId, setOperadorId] = useState('');
  const [previa, setPrevia] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const base = { tipo, cliente_id: clienteId, convenia_id: conveniaId };

  const carregar = useCallback(() => {
    if (!clienteId || !conveniaId) return;
    axios.get(`${API}/api/bporh/finalizacao/`, { params: { tipo, cliente_id: clienteId, convenia_id: conveniaId, _: Date.now() }, headers: headers() })
      .then((r) => { setInfo(r.data); setOperadorId(String(r.data.operador_sugerido_id || '')); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar a finalização'));
  }, [tipo, clienteId, conveniaId]);
  useEffect(() => { setModo(null); setPrevia(null); setMotivo(''); setMsg(''); setErro(''); carregar(); }, [carregar]);

  // vindo do pop-up de rescisão atrasada: abre direto o pedido ao gestor (uma vez)
  const jaAbriu = React.useRef(false);
  useEffect(() => {
    if (!abrirSolicitar || !info || jaAbriu.current) return;
    jaAbriu.current = true;
    const st = info.situacao?.status;
    if (st !== 'finalizada' && st !== 'solicitada') { setModo('solicitar'); setMotivo(''); }
  }, [abrirSolicitar, info]);

  const sit = info?.situacao;
  const finalizada = sit?.status === 'finalizada';
  const solicitada = sit?.status === 'solicitada';
  const devolvida = sit?.status === 'devolvida';

  const verPrevia = () => {
    setOcupado(true); setErro('');
    axios.post(`${API}/api/bporh/finalizacao/`, { ...base, acao: 'previa', motivo, operador_id: operadorId || undefined, pendentes },
      { headers: headers() })
      .then((r) => setPrevia(r.data.previa))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'))
      .finally(() => setOcupado(false));
  };
  const enviar = () => {
    setOcupado(true); setErro('');
    axios.post(`${API}/api/bporh/finalizacao/`, { ...base, acao: 'solicitar', motivo, operador_id: operadorId || undefined, pendentes },
      { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setModo(null); setPrevia(null); carregar(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível enviar'))
      .finally(() => setOcupado(false));
  };
  const decidir = (acao) => {
    setOcupado(true); setErro('');
    axios.post(`${API}/api/bporh/finalizacao/`, { ...base, acao, motivo, obs: motivo, sugestao: motivo }, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setModo(null); carregar(); if (onMudou && acao !== 'devolver') onMudou(acao); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível concluir'))
      .finally(() => setOcupado(false));
  };

  if (!info) return null;
  const rot = ROTULO[tipo];

  return (
    <div className="border rounded p-3 mb-3" style={{ background: finalizada ? '#e6f7ef' : '#f8f9fb', borderColor: finalizada ? '#a8dcc3' : '#e3e8ef' }}>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div style={{ minWidth: 0 }}>
          <div className="fw-semibold" style={{ color: finalizada ? '#116b47' : '#212529' }}>
            <i className={`fas ${finalizada ? 'fa-circle-check' : 'fa-flag-checkered'} me-2`}></i>
            {finalizada ? `${rot.charAt(0).toUpperCase() + rot.slice(1)} finalizada manualmente` : 'Finalização manual'}
          </div>
          {finalizada && (
            <div className="small mt-1" style={{ color: '#116b47' }}>
              por <b>{sit.finalizado_por}</b> em {sit.finalizado_em}{sit.motivo ? <> · motivo: {sit.motivo}</> : null}
            </div>
          )}
          {solicitada && (
            <div className="small mt-1" style={{ color: '#7a5d00' }}>
              <i className="fas fa-hourglass-half me-1"></i>
              Solicitada por <b>{sit.solicitado_por}</b> em {sit.solicitado_em} · aguardando o adm · motivo: {sit.motivo}
            </div>
          )}
          {devolvida && (
            <div className="small mt-2 p-2 rounded" style={{ background: '#fff8e6', borderLeft: '3px solid #f1be48', color: '#5c4400' }}>
              <i className="fas fa-reply me-1"></i>
              <b>Devolvida por {sit.devolvido_por}</b> em {sit.devolvido_em} — orientação: {sit.sugestao_gestor}
              {sit.motivo ? <div className="mt-1" style={{ color: '#7a6a3a' }}>Pedido original: {sit.motivo}</div> : null}
            </div>
          )}
          {!finalizada && !solicitada && !devolvida && (
            <div className="small text-muted mt-1">
              Use quando esta {rot} não vai ser concluída (desistência, recusa em assinar). O gestor recebe o pedido por e-mail.
            </div>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {!finalizada && modo === null && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setModo('solicitar'); setMotivo(sit?.motivo || ''); }}>
              <i className="fas fa-paper-plane me-1"></i>{solicitada ? 'Solicitar de novo' : 'Solicitar finalização'}
            </button>
          )}
          {solicitada && info.pode_finalizar && modo === null && (
            <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => { setModo('devolver'); setMotivo(''); }}
              title="Devolve à operadora com uma orientação de como resolver a pendência">
              <i className="fas fa-reply me-1"></i>Devolver à operadora
            </button>
          )}
          {!finalizada && info.pode_finalizar && modo === null && (
            <button type="button" className="btn btn-sm btn-success" onClick={() => { setModo('finalizar'); setMotivo(sit?.motivo || ''); }}>
              <i className="fas fa-lock me-1"></i>Finalizar manual
            </button>
          )}
          {finalizada && info.pode_finalizar && modo === null && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => { setModo('reabrir'); setMotivo(''); }}>
              <i className="fas fa-rotate-left me-1"></i>Reabrir
            </button>
          )}
        </div>
      </div>

      {erro && <div className="alert alert-danger py-2 mt-2 mb-0 small" role="button" onClick={() => setErro('')}>{erro}</div>}
      {msg && <div className="alert alert-success py-2 mt-2 mb-0 small" role="button" onClick={() => setMsg('')}>{msg}</div>}

      {modo === 'solicitar' && (
        <div className="mt-3">
          <label className="form-label small fw-bold mb-1">Motivo <span className="text-danger">*</span></label>
          <textarea className="form-control form-control-sm" rows={2} value={motivo}
            placeholder="Ex.: colaborador não quis assinar a rescisão" onChange={(e) => { setMotivo(e.target.value); setPrevia(null); }} />
          <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
            <div>
              <label className="form-label small fw-bold mb-1">Enviar como</label>
              <select className="form-select form-select-sm" value={operadorId} onChange={(e) => { setOperadorId(e.target.value); setPrevia(null); }}>
                <option value="">Escolha a operadora...</option>
                {(info.operadores || []).map((o) => (
                  <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{!o.pronto ? ' (sem senha de app)' : ''}</option>
                ))}
              </select>
            </div>
            <div className="small text-muted">Para: <b>{info.gestor_email}</b></div>
            <div className="ms-auto d-flex gap-2">
              <button type="button" className="btn btn-sm btn-light border" onClick={() => { setModo(null); setPrevia(null); }} disabled={ocupado}>Cancelar</button>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={verPrevia} disabled={ocupado || motivo.trim().length < 5}>
                <i className={`fas ${ocupado && !previa ? 'fa-spinner fa-spin' : 'fa-eye'} me-1`}></i>Ver prévia
              </button>
            </div>
          </div>
          {previa && (
            <div className="mt-3 border rounded bg-white">
              <div className="small px-3 py-2 border-bottom bg-light">
                <div><b>Assunto:</b> {previa.assunto}</div>
                <div><b>De:</b> {previa.remetente} · <b>Para:</b> {previa.para}</div>
              </div>
              <iframe title="Prévia da solicitação" srcDoc={previa.corpo_html} sandbox="" style={{ width: '100%', height: 380, border: 'none', background: '#f4f6fa' }} />
              <div className="d-flex justify-content-end gap-2 p-2 border-top">
                <button type="button" className="btn btn-sm btn-primary" onClick={enviar} disabled={ocupado || !previa.pronto}
                  title={previa.pronto ? 'Envia exatamente o que está na prévia' : 'Escolha uma operadora com senha de app'}>
                  <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`}></i>Enviar ao gestor
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {modo === 'devolver' && (
        <div className="mt-3">
          <label className="form-label small fw-bold mb-1">
            Como a operadora pode resolver esta pendência? <span className="text-danger">*</span>
          </label>
          <textarea className="form-control form-control-sm" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: ligue para o colaborador e agende a assinatura na quinta; se não aparecer, mande o termo por e-mail com AR" />
          <div className="small mt-2" style={{ color: '#7a5d00' }}>
            A {rot} continua pendente. A operadora que pediu vê esta orientação num aviso ao entrar no BPO RH.
          </div>
          <div className="d-flex justify-content-end gap-2 mt-2">
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setModo(null)} disabled={ocupado}>Cancelar</button>
            <button type="button" className="btn btn-sm btn-warning" onClick={() => decidir('devolver')}
              disabled={ocupado || motivo.trim().length < 5}>
              <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-reply'} me-1`}></i>Devolver com a orientação
            </button>
          </div>
        </div>
      )}

      {(modo === 'finalizar' || modo === 'reabrir') && (
        <div className="mt-3">
          <label className="form-label small fw-bold mb-1">
            {modo === 'finalizar' ? 'Motivo da finalização' : 'Por que reabrir?'}
          </label>
          <textarea className="form-control form-control-sm" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder={modo === 'finalizar' ? 'Ex.: colaborador desistiu antes da admissão' : 'Ex.: colaborador voltou atrás e vai assinar'} />
          <div className="small mt-2" style={{ color: modo === 'finalizar' ? '#116b47' : '#a11d2b' }}>
            {modo === 'finalizar'
              ? `A ${rot} passa a contar como concluída e sai do Painel, da janela de atrasos e do boletim do gestor.`
              : `A ${rot} volta a contar como pendente, com as tarefas como estavam.`}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-2">
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setModo(null)} disabled={ocupado}>Cancelar</button>
            <button type="button" className={`btn btn-sm ${modo === 'finalizar' ? 'btn-success' : 'btn-danger'}`}
              onClick={() => decidir(modo)} disabled={ocupado || (modo === 'finalizar' && motivo.trim().length < 5)}>
              <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : (modo === 'finalizar' ? 'fa-lock' : 'fa-rotate-left')} me-1`}></i>
              {modo === 'finalizar' ? 'Confirmar finalização' : 'Confirmar reabertura'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
